// src/profile/services/profileService.ts
// Service for fetching user profiles (read-only)

import { db } from '../../lib/firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { getPrivateProfile, type PrivateProfileKind } from '../../lib/privateProfile';

export interface UserProfile {
  uid: string;
  email?: string;
  displayName: string;
  username?: string;
  photoURL: string;
  role: 'creator' | 'viewer';
  createdAt: string | null;
  youtubeLinked: boolean;
  channelId?: string;
  channelName?: string;
  firstName?: string;
  lastName?: string;
  bio?: string;
}

export interface ActivityItem {
  id: string;
  type: 'video' | 'short' | 'live' | 'event';
  title: string;
  thumbnail?: string;
  createdAt: string;
  viewCount?: number;
}

/**
 * Fetch a user profile by their uid
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    // Try creators collection first
    let snap = await getDoc(doc(db, 'creators', userId));
    let role: 'creator' | 'viewer' = 'creator';
    let kind: PrivateProfileKind = 'creators';

    if (!snap.exists()) {
      // Try viewers collection
      snap = await getDoc(doc(db, 'viewers', userId));
      role = 'viewer';
      kind = 'viewers';
    }

    if (!snap.exists()) {
      // Try generic users collection as fallback
      snap = await getDoc(doc(db, 'users', userId));
      role = 'viewer';
      kind = 'users';
    }

    if (!snap.exists()) {
      return null;
    }

    const data = snap.data();
    // T1c (LAUNCH_PLAN.md): PII (email/firstName/lastName) merged from
    // <kind>/{uid}/private/profile when readable. Rule allows owner + admin;
    // every other caller silently gets null and falls back to parent values.
    const priv = await getPrivateProfile(userId, kind).catch(() => null);
    const firstName = priv?.firstName || data.firstName || '';
    const lastName  = priv?.lastName  || data.lastName  || '';
    const email     = priv?.email     || data.email;
    // T1e: fallback chain for non-owner views (post-T1f, real names live only
    // in private/profile which non-owners can't read). channelName / username
    // / role label keep the UI populated without leaking PII.
    const displayName = data.displayName ||
      (firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName) ||
      data.channelName ||
      data.username ||
      (role === 'creator' ? 'Creator' : 'Viewer');

    return {
      uid: userId,
      email,
      displayName,
      username: data.username,
      photoURL: data.photoURL || '/images/avatar-placeholder.svg',
      role,
      createdAt: data.createdAt || null,
      youtubeLinked: !!data.channelId,
      channelId: data.channelId,
      channelName: data.channelName,
      firstName,
      lastName,
      bio: data.bio,
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
}

/**
 * Fetch user's uploaded videos (activity history)
 */
export async function getUserVideos(userId: string, maxItems = 10): Promise<ActivityItem[]> {
  try {
    const videosRef = collection(db, 'videos');
    const q = query(
      videosRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );

    const snap = await getDocs(q);
    return snap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        type: 'video' as const,
        title: data.title || 'Untitled Video',
        thumbnail: data.thumbnail,
        createdAt: data.createdAt,
        viewCount: data.viewCount || 0,
      };
    });
  } catch (error) {
    console.error('Error fetching user videos:', error);
    return [];
  }
}

/**
 * Fetch user's live events participated/hosted
 */
export async function getUserLiveActivity(userId: string, maxItems = 10): Promise<ActivityItem[]> {
  try {
    // Check liveEvents collection for hosted events
    const eventsRef = collection(db, 'liveEvents');
    const q = query(
      eventsRef,
      where('hostId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );

    const snap = await getDocs(q);
    return snap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        type: 'event' as const,
        title: data.title || 'Untitled Event',
        thumbnail: data.thumbnail,
        createdAt: data.scheduledAt || data.createdAt,
      };
    });
  } catch (error) {
    console.error('Error fetching user live activity:', error);
    return [];
  }
}

/**
 * Resolve a username to a user ID
 * Searches across creators, viewers, and users collections
 */
export async function resolveUsername(username: string): Promise<string | null> {
  const normalizedUsername = username.toLowerCase();

  try {
    // Search creators
    const creatorsRef = collection(db, 'creators');
    let q = query(creatorsRef, where('username', '==', normalizedUsername), limit(1));
    let snap = await getDocs(q);
    if (!snap.empty) return snap.docs[0].id;

    // Search viewers
    const viewersRef = collection(db, 'viewers');
    q = query(viewersRef, where('username', '==', normalizedUsername), limit(1));
    snap = await getDocs(q);
    if (!snap.empty) return snap.docs[0].id;

    // Search users
    const usersRef = collection(db, 'users');
    q = query(usersRef, where('username', '==', normalizedUsername), limit(1));
    snap = await getDocs(q);
    if (!snap.empty) return snap.docs[0].id;

    return null;
  } catch (error) {
    console.error('Error resolving username:', error);
    return null;
  }
}

/**
 * Check if a username is available
 */
export async function isUsernameAvailable(username: string): Promise<boolean> {
  const userId = await resolveUsername(username);
  return userId === null;
}

/**
 * Fetch profile by username (convenience function)
 */
export async function getUserProfileByUsername(username: string): Promise<UserProfile | null> {
  const userId = await resolveUsername(username);
  if (!userId) return null;
  return getUserProfile(userId);
}
